import React, { useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import CopilotAssistant from "@/components/CopilotAssistant";

interface TestMessage {
  role: "user" | "workflow" | "system";
  text: string;
  timestamp: Date;
}

interface TestResponse {
  messages: string[];
  status: string;
  quickReplies: string[];
  quickReplyValues: string[];
  questionType: string;
  teamId: number | null;
  agentId: number | null;
  closeConversation: boolean;
  sessionState: string;
  error: string | null;
}

interface Props {
  workflowId: string;
  workflowName: string;
  onClose: () => void;
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-2 mb-3">
      <div className="w-6 h-6 rounded-full bg-cornflower-blue-100 flex items-center justify-center flex-shrink-0 text-cornflower-blue-600 text-xs font-bold">
        W
      </div>
      <div className="bg-slate-100 rounded-2xl rounded-tl-none px-3 py-2">
        <div className="flex gap-1 items-center h-4">
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

function TestBubble({ message }: { message: TestMessage }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[80%]">
          <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-none px-3 py-2 text-sm leading-relaxed">
            {message.text}
          </div>
        </div>
      </div>
    );
  }

  if (message.role === "system") {
    const isTest = message.text.startsWith("[Test]");
    return (
      <div className="flex justify-center mb-2">
        <div className={`text-xs px-3 py-1 rounded-full ${isTest ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-500"}`}>
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 mb-3">
      <div className="w-6 h-6 rounded-full bg-cornflower-blue-100 flex items-center justify-center flex-shrink-0 text-cornflower-blue-600 text-xs font-bold">
        W
      </div>
      <div className="max-w-[80%]">
        <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-tl-none px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap">
          {message.text}
        </div>
      </div>
    </div>
  );
}

export default function WorkflowTestPanel({ workflowId, workflowName, onClose }: Props) {
  const api = useApi();
  const [messages, setMessages] = useState<TestMessage[]>([]);
  const [sessionState, setSessionState] = useState<string | null>(null);
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [quickReplyValues, setQuickReplyValues] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);
  const [copilotPrefill, setCopilotPrefill] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-start the workflow when the panel opens
  useEffect(() => {
    startTest();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isRunning]);

  async function runStep(utterance: string, currentSession: string | null) {
    setIsRunning(true);
    setQuickReplies([]);

    try {
      const result = await api.messaging.post(`Workflows/${workflowId}/Test`, {
        utterance,
        sessionState: currentSession
      });

      if (!result.ok) {
        addSystemMessage("Error: could not reach the test endpoint.");
        setIsRunning(false);
        return;
      }

      const data: TestResponse = await result.json();
      setSessionState(data.sessionState ?? null);

      // Add workflow messages as bubbles
      const newMsgs: TestMessage[] = [];
      for (const m of data.messages ?? []) {
        if (m.startsWith("[Test]")) {
          newMsgs.push({ role: "system", text: m, timestamp: new Date() });
        } else {
          newMsgs.push({ role: "workflow", text: m, timestamp: new Date() });
        }
      }
      setMessages((prev) => [...prev, ...newMsgs]);

      // Handle terminal statuses
      if (data.status === "done" || data.closeConversation) {
        addSystemMessage("Workflow completed.");
        setIsDone(true);
      } else if (data.status === "error") {
        addSystemMessage(`Error: ${data.error || "Unknown error"}`);
        setIsDone(true);
      } else if (data.status === "waitForUser") {
        setQuickReplies(data.quickReplies ?? []);
        setQuickReplyValues(data.quickReplyValues ?? []);
        inputRef.current?.focus();
      }
    } catch {
      addSystemMessage("Network error — is the messaging API running?");
      setIsDone(true);
    } finally {
      setIsRunning(false);
    }
  }

  function addSystemMessage(text: string) {
    setMessages((prev) => [...prev, { role: "system", text, timestamp: new Date() }]);
  }

  function startTest() {
    setMessages([]);
    setSessionState(null);
    setQuickReplies([]);
    setIsDone(false);
    setInput("");
    runStep("", null);
  }

  async function sendMessage(text?: string) {
    const utterance = (text ?? input).trim();
    if (!utterance || isRunning || isDone) return;

    setInput("");
    setQuickReplies([]);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: utterance, timestamp: new Date() }
    ]);

    await runStep(utterance, sessionState);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function buildCopilotPrefill(): string {
    const transcript = messages
      .map((m) => {
        if (m.role === "user") return `You: ${m.text}`;
        if (m.role === "workflow") return `Workflow: ${m.text}`;
        return `[${m.text}]`;
      })
      .join("\n");

    return `I'm testing my workflow "${workflowName}" and it's not behaving as expected.\n\nHere's what happened during the test:\n\n${transcript}\n\nCan you help me understand what might be wrong and how to fix it?`;
  }

  function handleAskCopilot() {
    setCopilotPrefill(buildCopilotPrefill());
    setShowCopilot(true);
  }

  return (
    <>
      {/* Slide-in test panel — fixed right side, full height */}
      <div className="fixed right-0 top-0 h-full w-[380px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-sm font-semibold text-slate-700">Test Workflow</span>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              Not saved
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={startTest}
              disabled={isRunning}
              className="text-xs text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-40"
              title="Restart test"
              aria-label="Restart test"
            >
              ↺ Restart
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors text-lg leading-none"
              aria-label="Close test panel"
            >
              ×
            </button>
          </div>
        </div>

        {/* Workflow name */}
        <div className="px-4 py-2 bg-cornflower-blue-50 border-b border-cornflower-blue-100">
          <p className="text-xs text-cornflower-blue-700 font-medium truncate">{workflowName}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            Side effects (email, CRM, payment) are simulated — no real data sent.
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-3" style={{ minHeight: 0 }}>
          {messages.length === 0 && isRunning && (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
              Starting workflow…
            </div>
          )}

          {messages.map((msg, i) => (
            <TestBubble key={i} message={msg} />
          ))}

          {isRunning && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick replies */}
        {quickReplies.length > 0 && !isDone && (
          <div className="px-3 pb-2 flex flex-wrap gap-1.5">
            {quickReplies.map((label, i) => (
              <button
                key={i}
                onClick={() => sendMessage(quickReplyValues[i] ?? label)}
                disabled={isRunning}
                className="text-xs border border-indigo-300 text-indigo-600 hover:bg-indigo-50 px-3 py-1.5 rounded-full transition-colors disabled:opacity-40"
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        {!isDone ? (
          <div className="px-3 pb-3">
            <div className="border border-slate-200 rounded-xl bg-slate-50 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a reply…"
                rows={2}
                disabled={isRunning}
                className="w-full bg-transparent resize-none px-3 pt-2.5 pb-1 text-sm text-slate-800 placeholder-slate-400 outline-none disabled:opacity-40"
                aria-label="Test reply input"
              />
              <div className="flex items-center justify-end px-2 pb-2">
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isRunning}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                  aria-label="Send"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-3 pb-3">
            <button
              onClick={startTest}
              className="w-full text-sm text-indigo-600 border border-indigo-200 rounded-xl py-2.5 hover:bg-indigo-50 transition-colors"
            >
              ↺ Run test again
            </button>
          </div>
        )}

        {/* Ask Copilot footer */}
        {messages.length > 1 && (
          <div className="px-3 pb-4 pt-1 border-t border-slate-100">
            <button
              onClick={handleAskCopilot}
              className="w-full flex items-center justify-center gap-2 text-xs text-slate-500 hover:text-indigo-600 py-2 rounded-lg hover:bg-slate-50 transition-colors"
              aria-label="Ask Copilot for help with this workflow"
            >
              <span className="text-base">✦</span>
              Not working as expected? Ask Copilot for help
            </button>
          </div>
        )}
      </div>

      {/* Copilot panel — opens on top, pre-filled with test transcript */}
      {showCopilot && (
        <CopilotAssistant
          context={`workflows — User is testing the workflow named "${workflowName}". Help them debug it based on their test transcript.`}
          prefillMessage={copilotPrefill}
          onClose={() => setShowCopilot(false)}
        />
      )}
    </>
  );
}
